import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Eye, EyeOff, AlertCircle } from 'lucide-react'
import Logo from '../components/ui/Logo'

export default function LoginPage() {
  const { signIn, profile } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw]     = useState(false)
  const [error, setError]       = useState('')
  const [loading, setLoading]   = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await signIn(email, password)
      // Role-based redirect — profile loads async via useAuth;
      // a short wait then check role from Supabase directly
      const { supabase } = await import('../lib/supabase')
      const { data: { user } } = await supabase.auth.getUser()
      const { data: prof } = await supabase.from('profiles').select('role').eq('id', user?.id).single()
      if (prof?.role === 'caja_user') navigate('/caja')
      else navigate('/')
    } catch (err) {
      setError('Correo o contraseña incorrectos. Por favor verifica tus datos.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header con logo */}
        <div className="text-center mb-8 flex flex-col items-center">
          <Logo variant="mark" className="text-cream-100 mb-4" size={64} />
          <h1 className="font-display text-3xl text-cream-100 tracking-wider leading-none mb-1">NEXT VALLEY</h1>
          <p className="text-accent-400 text-[10px] font-semibold uppercase tracking-[0.3em]">Strategic Partners</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Iniciar sesión</h2>

          {error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-5 text-sm">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Correo electrónico</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input"
                placeholder="tu@correo.com"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="label">Contraseña</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="input pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-2.5 text-base mt-2"
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-100 text-center space-y-4">
            <p className="text-xs text-gray-400">
              ¿Eres consultor NVC? Contacta al administrador para obtener acceso.
            </p>
            <Link to="/register"
              className="flex items-center justify-between gap-3 bg-gradient-to-r from-accent-400 to-yellow-400 rounded-xl px-4 py-3 group hover:shadow-md transition-all">
              <div className="text-left">
                <p className="text-xs font-bold text-brand-900 leading-tight">¿Quieres hacer tu plan estratégico?</p>
                <p className="text-xs text-brand-800 opacity-80 leading-tight mt-0.5">Empieza gratis, sin consultor</p>
              </div>
              <span className="text-brand-900 font-bold text-sm whitespace-nowrap group-hover:translate-x-0.5 transition-transform">
                Comenzar →
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
